import { cn } from "@/lib/utils";

export interface StatusOption {
  label: string;
  value: string;
  count?: number;
  icon?: React.ReactNode;
}

interface StatusFilterProps {
  value: string | null;
  onChange: (value: string | null) => void;
  options: StatusOption[];
  className?: string;
  label?: string;
}

export function StatusFilter({ value, onChange, options, className, label }: StatusFilterProps) {
  const totalCount = options.reduce((sum, opt) => sum + (opt.count || 0), 0);

  return (
    <div className={cn("inline-flex items-center rounded-lg bg-muted/50 p-1", className)}>
      {/* All option */}
      <button
        onClick={() => onChange(null)}
        className={cn(
          "relative flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-all duration-200",
          value === null
            ? "bg-background text-foreground shadow-sm"
            : "text-muted-foreground hover:text-foreground hover:bg-background/50"
        )}
      >
        All
        {totalCount > 0 && (
          <span
            className={cn(
              "ml-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums",
              value === null
                ? "bg-primary/10 text-primary"
                : "bg-muted text-muted-foreground"
            )}
          >
            {totalCount}
          </span>
        )}
      </button>

      {/* Separator */}
      <div className="mx-1 h-4 w-px bg-border" />

      {/* Filter options */}
      {options.map((option) => (
        <button
          key={option.value}
          onClick={() => onChange(value === option.value ? null : option.value)}
          className={cn(
            "relative flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-all duration-200",
            value === option.value
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground hover:bg-background/50"
          )}
        >
          {option.icon}
          {option.label}
          {option.count !== undefined && option.count > 0 && (
            <span
              className={cn(
                "ml-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums",
                value === option.value
                  ? "bg-primary/10 text-primary"
                  : "bg-muted text-muted-foreground"
              )}
            >
              {option.count}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

// Segmented filter variant - more prominent, good for primary filters
interface SegmentedFilterProps {
  value: string | null;
  onChange: (value: string | null) => void;
  options: StatusOption[];
  className?: string;
  showAllOption?: boolean;
}

export function SegmentedFilter({
  value,
  onChange,
  options,
  className,
  showAllOption = true,
}: SegmentedFilterProps) {
  const totalCount = options.reduce((sum, opt) => sum + (opt.count || 0), 0);

  return (
    <div
      className={cn(
        "inline-flex items-center gap-1 rounded-xl border border-border/50 bg-muted/30 p-1 backdrop-blur-sm",
        className
      )}
    >
      {showAllOption && (
        <button
          onClick={() => onChange(null)}
          className={cn(
            "relative rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200",
            value === null
              ? "bg-background text-foreground shadow-md shadow-black/5"
              : "text-muted-foreground hover:text-foreground"
          )}
        >
          <span className="relative z-10">All</span>
          {totalCount > 0 && (
            <span
              className={cn(
                "ml-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full text-[11px] font-semibold tabular-nums",
                value === null
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted-foreground/20 text-muted-foreground"
              )}
            >
              {totalCount}
            </span>
          )}
        </button>
      )}

      {options.map((option, idx) => (
        <button
          key={option.value}
          onClick={() => onChange(value === option.value ? null : option.value)}
          className={cn(
            "relative rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200",
            value === option.value
              ? "bg-background text-foreground shadow-md shadow-black/5"
              : "text-muted-foreground hover:text-foreground"
          )}
        >
          <span className="relative z-10 flex items-center gap-1.5">
            {option.icon}
            {option.label}
          </span>
          {option.count !== undefined && option.count > 0 && (
            <span
              className={cn(
                "ml-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full text-[11px] font-semibold tabular-nums",
                value === option.value
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted-foreground/20 text-muted-foreground"
              )}
            >
              {option.count}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

// Chip filter variant - compact, good for secondary/multiple filters
interface ChipFilterProps {
  label?: string;
  value: string | null;
  onChange: (value: string | null) => void;
  options: StatusOption[];
  className?: string;
}

export function ChipFilter({ label, value, onChange, options, className }: ChipFilterProps) {
  return (
    <div className={cn("flex items-center gap-3", className)}>
      {label && (
        <span className="text-sm font-medium text-muted-foreground">{label}</span>
      )}
      <div className="flex flex-wrap items-center gap-1.5">
        <button
          onClick={() => onChange(null)}
          className={cn(
            "inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium transition-all duration-200",
            value === null
              ? "border-primary bg-primary/10 text-primary"
              : "border-border bg-background text-muted-foreground hover:border-primary/50 hover:text-foreground"
          )}
        >
          All
        </button>
        {options.map((option) => (
          <button
            key={option.value}
            onClick={() => onChange(value === option.value ? null : option.value)}
            className={cn(
              "inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium transition-all duration-200",
              value === option.value
                ? "border-primary bg-primary/10 text-primary"
                : "border-border bg-background text-muted-foreground hover:border-primary/50 hover:text-foreground"
            )}
          >
            {option.icon}
            {option.label}
            {option.count !== undefined && option.count > 0 && (
              <span
                className={cn(
                  "ml-0.5 tabular-nums",
                  value === option.value ? "text-primary" : "text-muted-foreground/70"
                )}
              >
                ({option.count})
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

// Common status options for entities
export const entityStatusOptions: StatusOption[] = [
  { label: "Active", value: "ACTIVE" },
  { label: "Draft", value: "DRAFT" },
  { label: "Inactive", value: "INACTIVE" },
];

export const requestStatusOptions: StatusOption[] = [
  { label: "Pending", value: "PENDING" },
  { label: "Approved", value: "APPROVED" },
  { label: "Rejected", value: "REJECTED" },
];

export const cardStatusOptions: StatusOption[] = [
  { label: "Active", value: "ACTIVE" },
  { label: "Frozen", value: "FROZEN" },
  { label: "Cancelled", value: "CANCELLED" },
  { label: "Expired", value: "EXPIRED" },
];
